// TMOS Log Viewer Service
// Date: November 15, 2025
// Purpose: Frontend service for system logs API

import api from './api';

export interface LogEntry {
  id?: string;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  source: 'database' | 'file';
  tenant_id?: string;
  user_id?: string;
  resource_type?: string;
  resource_id?: string;
  action?: string;
  metadata?: any;
  ip_address?: string;
  user_agent?: string;
}

export interface LogFilters {
  source?: 'database' | 'file' | 'all';
  level?: 'info' | 'warn' | 'error' | 'debug';
  start_date?: string;
  end_date?: string;
  user_id?: string;
  resource_type?: string;
  action?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface LogStatistics {
  total: number;
  by_level: Record<string, number>;
  by_source: Record<string, number>;
  by_resource_type: Record<string, number>;
}

class LogViewerService {
  /**
   * Get logs with filters (admin only)
   */
  async getLogs(filters: LogFilters = {}): Promise<LogEntry[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/admin/logs${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get log statistics (admin only)
   */
  async getStatistics(filters: Omit<LogFilters, 'limit' | 'offset'> = {}): Promise<LogStatistics> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/admin/logs/statistics${queryString ? `?${queryString}` : ''}`);
    return response.data.data;
  }

  /**
   * Get available log sources (admin only)
   */
  async getSources(): Promise<string[]> {
    const response = await api.get('/api/v1/admin/logs/sources');
    return response.data.data || [];
  }

  /**
   * Get file logs (admin only)
   */
  async getFileLogs(filters: Omit<LogFilters, 'source'> = {}): Promise<LogEntry[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/admin/logs/file-logs${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }
}

export const logViewerService = new LogViewerService();


